import { Injectable, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { combineLatest, map, Observable } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { sortBy, uniqueBy } from '@fcom/core/utils/utils';
import { SelectOption } from '@fcom/common/interfaces/form-interface';

export interface TranslatedCountryCode {
  countryCode: string;
  firstOption: string;
  name: string;
  nationality?: string;
  phonePrefix: string;
}

const countryCodeToOption = (cc: TranslatedCountryCode): SelectOption =>
  cc && {
    name: `${cc.name} (+${cc.phonePrefix})`,
    value: `${cc.countryCode}|${cc.phonePrefix}`,
  };

const matchingCountryCode =
  (countryCode = '') =>
  (cc: TranslatedCountryCode) =>
    countryCode.toLowerCase() === (cc.countryCode || '').toLowerCase();

@Injectable()
export class PhonePrefixService {
  private languageService = inject(LanguageService);

  readonly countryCodes$: Observable<TranslatedCountryCode[]> =
    this.languageService.translate<TranslatedCountryCode[]>('countryCodes');
  readonly countryCodes: Signal<TranslatedCountryCode[] | undefined> = toSignal(this.countryCodes$);

  SEPARATOR: SelectOption = {
    name: '-------------------',
    value: '-------------------',
    disabled: true,
  };
  DEFAULT: SelectOption;

  setupPhonePrefixes(showDefault = true): Observable<SelectOption[]> {
    return combineLatest([
      this.countryCodes$,
      this.languageService.translate('selectPhonePrefix'),
      this.languageService.countryCode,
    ]).pipe(
      map(([countryCodes, defaultOption, activeCountry]: [TranslatedCountryCode[], string, string]) => {
        this.DEFAULT = {
          name: defaultOption,
          value: null,
        };
        const baseOptions = sortBy(countryCodes.map(countryCodeToOption), (so) => so.name);
        const optionsBasedOnLocations = [countryCodeToOption(countryCodes.find(matchingCountryCode(activeCountry)))]
          .filter(Boolean)
          .filter(uniqueBy((country) => country['value']));
        const options = [...optionsBasedOnLocations, this.SEPARATOR, ...baseOptions];
        return optionsBasedOnLocations.length === 0 ? baseOptions : showDefault ? [this.DEFAULT, ...options] : options;
      })
    );
  }

  getCountryCodeFromPhonePrefix(phonePrefix: number | undefined): string {
    return (
      this.countryCodes()?.find((countryCode) => countryCode.phonePrefix === phonePrefix?.toString())?.countryCode ?? ''
    );
  }
}
